import React, { useContext, useEffect, useState } from 'react';
import {
  ComponentParams,
  ComponentRendering,
  ComponentFactory,
  SitecoreContext,
  Field,
  LinkFieldValue,
  ImageField,
  RichText,
} from '@sitecore-jss/sitecore-jss-react';

import './index.scss';
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Dialog,
  DialogActions,
  DialogTitle,
  Paper,
} from '@mui/material';
import { makeStyles, Theme, styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import PreviewStandardImage, {
  PreviewStandardImageDetails,
} from '../../PreviewStandardImage/PreviewStandardImage';
import FormControl from '@mui/material/FormControl';
import NativeSelect from '@mui/material/NativeSelect';
import InputBase from '@mui/material/InputBase';
import CustomTooltip from '../../CustomTooltip/CustomTooltip';
import { deleteAPICall, getAPICall, postAPICall } from '../../../dataFetcher/axiosMethodCalls';
import { APIWebstoreContext } from '../../../context/globalContext';
import Quantity, { QuantityFieldsProps } from '../../Quantity/Quantity';
import { addEllipsis, getCurrencyInUSD } from '../../../utils/helper';
import RichToastNotification, {
  RichToastMesssageProps,
} from '../../RichToastNotification/RichToastNotification';
import RecipientEmail from '../../RecipientEmail/RecipientEmail';
import LoadingSkeleton from '../../LoadingSkeleton/LoadingSkeleton';
import nextId from 'react-id-generator';
import { getCountryConfigFields } from '../../../utils/countryConfigurationHelper';
import { getFieldSpecificConfiguration } from '../../../utils/fieldSpecificConfigHelper';
import { IFieldSpecificConfig } from '../../../model/IFieldSpecificConfig';
import { ProductType } from '../../../utils/ProductTypes';
import { ReportType } from '../../../utils/ReportTypes';
import { LOB_DISPLAY_NAMES, STANDARDS_IDENTIFIER } from '../../../utils/constants';
import { removeCartEvent, getTimeInCart, viewCartEvent } from '../../../utils/analyticsHelper';
import { getIdpToken } from '../../../utils/authHelper';
import { SessionOptions } from '../ProductSalesPrice/ProductSalesPrice';

export type FormatTextFieldProps = {
  FormatType: Field<string>;
};

export type FormatFieldsProps = {
  fields: FormatTextFieldProps;
  displayName: string;
  name: string;
};

export type FormatsProps = {
  Formats: Array<FormatFieldsProps>;
};

export type FormatOptionsFieldsProps = {
  fields: FormatsProps;
  name: string;
};

export type StandardProductCartDetailsProps = {
  fields: {
    Link: Field<LinkFieldValue>;
    FormatTooltipText: Field<string>;
    OptInUpgradeAgreementText: Field<string>;
    OptInUpgradeTooltipText: Field<string>;
    TypeSpecificFormats: Array<FormatOptionsFieldsProps>;
    ThumbnailImage: ImageField;
    confirmationMessage: Field<string>;
    okButtonText: Field<string>;
    cancelButtonText: Field<string>;
    ToastMessageForRemoveItem: Field<string>;
    RecipientEmailTooltip: Field<string>;
    EmailPlaceholderText: Field<string>;
    RecipientEmailPlaceholderText: Field<string>;
    PreviewBaseUrl: Field<string>;
    ThumbnailBaseUrl: Field<string>;
    DatasheetImage: ImageField;
    CartEmptyText: Field<string>;
    Countries: Array<ProductFormatOptionsFieldProps>;
    StatisticsRepoFormatTooltipTxt: Field<string>;
    Fields: Array<IFieldSpecificConfig>;
    ThumbnailText: Field<string>;
    DatasheetThumbnailText: Field<string>;
  };

  previewStdFields: PreviewStandardImageDetails;
  quantityFields: QuantityFieldsProps;
  sitecoreContext: SitecoreContext & { pageEditing: boolean };
};

export type ProductFormatOptionsFieldProps = {
  displayName: string;
  fields: IFormatOptionFields;
  name: string;
};

export interface IFormatOptionFields {
  AllowedFormats: Array<FormatOptionsFieldsProps>;
}

export interface CartProductFieldsProps {
  title: string;
  productId: string;
  publicationNbr: string;
  edition: number;
  publishedDate: string;
  previewUrl: string;
  thumbnailUrl: string;
  status: string;
  isRedlineExist: boolean;
  isRedline: boolean;
  isRecent: boolean;
  isDatasheet: boolean;
  productType: string;
  publicationType: string;
  term: string;
  year: number;
  isStatisticsReport: boolean;
  examWindow: string;
  apiPoNumber: string;
  isSurchargeApplied: boolean;
  surchargeType: string;
  surchargeCost: number;
  isSurchargeWaivedOff: boolean;
  surchargeLineItemId: string;
  isExamWindowAvailable: boolean;
  applicationNumber: string;
  language: string;
  session: string;
  modality: string;
  reportType: string;
}

export interface CartPriceOptionFieldsProps {
  format: string;
  isActive: boolean;
  language: string;
  quantity: number;
  unitPrice: number;
  variantId: string;
}

export interface CartDetails {
  quantity: number;
  items: Array<CartItemsDetails>;
  lob: string;
}

export type CartItemsDetails = {
  orderId: string;
  lineItemId: string;
  product: CartProductFieldsProps;
  priceOption: CartPriceOptionFieldsProps;
  sendToEmailAddresses: Array<string>;
  sessionOptions?: SessionOptions;
  isValid: boolean;
  message: string;
  dateAdded?: string;
};

const Item = styled(Paper)(({ theme }) => ({
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

interface Props {
  children: React.ReactElement;
  componentFactory: ComponentFactory;
  rendering: ComponentRendering;
  params: ComponentParams;
}

const BootstrapInput = styled(InputBase)(({ theme }) => ({
  'label + &': {
    marginTop: theme.spacing(3),
  },
  '& .MuiInputBase-input': {
    borderRadius: 4,
    position: 'relative',
    backgroundColor: theme.palette.background.paper,
    border: '1px solid #ced4da',
    fontSize: 16,
    padding: '5px 26px 10px 12px',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    fontFamily: ['urw-din', 'sans-serif'].join(','),
    '&:focus': {
      borderRadius: 4,
      borderColor: '#80bdff',
      boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)',
    },
  },
}));

// export const useStyles = makeStyles((theme: Theme) =>
//   createStyles({
//     margin: {
//       margin: theme.spacing(1),
//     },
//     root: {
//       minWidth: 200,
//     },
//   })
// );
const StyledCard = styled(Card)(({ theme }) => ({
  minWidth: 200,
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  margin: theme.spacing(1),
}));

export const StandardProductCartDetails = ({
  fields,
}: StandardProductCartDetailsProps): React.JSX.Element => {
  const [orderID, setOrderID] = useState('');
  const [cartItemDetails, setCartItemDetails] = useState(Array<CartItemsDetails>());
  const { state, dispatch } = useContext(APIWebstoreContext);
  const [showToast, setShowToast] = useState(false);
  const [toastMessages, setToastMessages] = useState<Array<RichToastMesssageProps>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRedlineLoading, setIsRedlineLoading] = useState('');
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [countryConfigurationInfo, setCountryConfigurationInfo] = useState<IFormatOptionFields>();
  const [openDialog, setOpenDialog] = useState(false);
  const [lineItemId, setLineItemId] = useState<string>('');
  const [orderId, setOrderId] = useState<string>('');
  const [item, setItem] = useState<CartItemsDetails>();

  const handleOpenDialog = (item: any) => {
    setOpenDialog(true);
    setLineItemId(item.lineItemId);
    setOrderId(item.orderId);
    setItem(item);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleFormatChange = (
    event: React.ChangeEvent<{ value: string }>,
    item: CartItemsDetails
  ) => {
    item.priceOption.format = event.target.value;
    formatUpdatePostCall(event.target.value, item);
  };

  function handleRemoveItem() {
    setOpenDialog(false);
    removeLineItem();
  }

  async function removeLineItem() {
    try {
      await removeAPICall(orderId, lineItemId);
      if (item) {
        removeItem(item);
      }

      quantityUpdated();
      if (dispatch) {
        dispatch({
          type: 'CART_UPDATE',
          payload: {
            toggleCartQuantityLoad: !state?.toggleCartQuantityLoad,
            toggleOrderSummaryLoad: !state?.toggleOrderSummaryLoad,
          },
        });
      }
      removeCartEvent(item);
    } catch (error) {
      console.error('Error removing line item:', error);
    }
  }

  const removeAPICall = async (orderId: string, lineItemId: string) => {
    const url = `${process.env.REACT_APP_CART_URL}/CartOrder/${orderId}/lineitems/${lineItemId}`;

    try {
      const response = await deleteAPICall(url);
      if (response && response.status === 200) {
        return response;
      } else {
        throw new Error('Failed to remove item');
      }
    } catch (error) {
      throw error;
    }
  };

  const removeItem = (cartItem: CartItemsDetails) => {
    setCartItemDetails(cartItemDetails.filter((x) => x.lineItemId !== cartItem.lineItemId));

    if (toastMessages) {
      toastMessages.splice(0, toastMessages.length);
    }

    let messages = fields?.ToastMessageForRemoveItem.value.split('##').filter(Boolean);
    messages.map((item) => {
      if (item === 'PRODUCT_TITLE') {
        toastMessages.push({
          type: 'link',
          linkUrl: getProductUrl(cartItem.product),
          message: cartItem.product.title,
        });
      } else {
        toastMessages.push({
          type: 'text',
          message: item,
        });
      }
    });

    setToastMessages([...toastMessages]);
    setShowToast(true);
  };

  const toastRemove = () => {
    setShowToast(false);
  };

  const quantityUpdated = () => {
    setIsEmailLoading(true);
    fetchData();
    const maxcartItemQuantity = cartItemDetails.reduce(
      (op, item) => (op = op > item.priceOption.quantity ? op : item.priceOption.quantity),
      0
    );

    if (dispatch) {
      dispatch({
        type: 'CART_UPDATE',
        payload: { maxCartItemQuantity: maxcartItemQuantity },
      });
    }
  };

  const emailRemoved = () => {
    fetchData();
  };

  const getFieldSpecificConfig = (productType: string) => {
    let fieldConfigData = getFieldSpecificConfiguration(productType, fields?.Fields);
    const fieldSpecificConfiguration: IFieldSpecificConfig | undefined = fieldConfigData.map(
      (config) => {
        return {
          CartItemMaxQuantity: config.fields.CartItemMaxQuantity,
          EmailRecipientMaxQuantity: config.fields.EmailRecipientMaxQuantity,
          HideEdition: config.fields.HideEdition,
          HidePeriod: config.fields.HidePeriod,
        };
      }
    )[0];
    return fieldSpecificConfiguration;
  };

  function formatUpdatePostCall(format: string, item: CartItemsDetails) {
    const formatUpdateRequest = {
      OrderId: item.orderId,
      lineItemId: item.lineItemId,
      format,
    };

    postAPICall(
      `${process.env.REACT_APP_CART_URL}/CartOrder/updateFormat`,
      formatUpdateRequest
    ).then(function (response: any) {
      if (response && response.status === 200) {
        fetchData();
        if (dispatch) {
          dispatch({
            type: 'CART_UPDATE',
            payload: {
              toggleOrderSummaryLoad: !state?.toggleOrderSummaryLoad,
            },
          });
        }
      }
    });
  }

  function upgradeRedline(item: CartItemsDetails, e: any) {
    postAPICall(
      `${process.env.REACT_APP_CART_URL}/CartOrder/${item.orderId}/lineitems/${item.lineItemId}/redlineupgrade`,
      {}
    )
      .then(function (response: any) {
        if (response && response.status === 200) {
          fetchData();
          if (dispatch) {
            dispatch({
              type: 'CART_UPDATE',
              payload: { toggleOrderSummaryLoad: !state?.toggleOrderSummaryLoad },
            });
          }
        }
      })
      .finally(() => {
        setIsRedlineLoading('');
      });
  }

  function removeRedline(item: CartItemsDetails, e: any) {
    postAPICall(
      `${process.env.REACT_APP_CART_URL}/CartOrder/${item.orderId}/lineitems/${item.lineItemId}/removeredline`,
      {}
    )
      .then(function (response: any) {
        if (response && response.status === 200) {
          fetchData();
          if (dispatch) {
            dispatch({
              type: 'CART_UPDATE',
              payload: { toggleOrderSummaryLoad: !state?.toggleOrderSummaryLoad },
            });
          }
        }
      })
      .finally(() => {
        setIsRedlineLoading('');
      });
  }

  function toggleRedline(item: CartItemsDetails, e: any) {
    setIsRedlineLoading(item.lineItemId);
    if (e.target.checked) {
      upgradeRedline(item, e);
    } else {
      removeRedline(item, e);
    }
  }

  function getOptInUpgradeRedline(item: CartItemsDetails, isLoggedIn: boolean | undefined) {
    if ((item.product?.isRedlineExist || item.product?.isRedline) && isLoggedIn) {
      let isRedline = item.product?.isRedline;
      return (
        <div className="optInDiv" key={nextId()}>
          <div className="optInNotifications" key={nextId()}>
            {isRedlineLoading === item.lineItemId ? (
              <LoadingSkeleton theme="light" minWidth="39px" loaderHeight="22px" />
            ) : (
              <input
                type="checkbox"
                className="checkbox"
                defaultChecked={isRedline}
                onChange={(e) => toggleRedline(item, e)}
                id={item.lineItemId}
                key={nextId()}
              ></input>
            )}
            <label className="optinnotificationtext" key={nextId()}>
              <p className="optInCartFullText" key={nextId()}>
                <span key={nextId()}>
                  <strong>Upgrade</strong>
                  <span className="upgradeTextTitleEdition" key={nextId()}>
                    to {item.product?.title}, {item.product?.edition}
                    <span className="upgradeTextTitleEdition" key={nextId()}>
                      Edition with
                    </span>
                  </span>
                  <span className="OptinLabelRedline" key={nextId()}>
                    Redline
                  </span>{' '}
                  (adds 15% to price of standard)
                </span>
              </p>
            </label>
            <CustomTooltip
              fields={{
                'Tooltip Text': fields.OptInUpgradeTooltipText,
              }}
            />
            <br></br>
          </div>
        </div>
      );
    } else {
      return <></>;
    }
  }

  function getProductCartSkeleton() {
    return (
      <Grid className="productCartGrid" item xs={9} sm={6} md={3}>
        <StyledCard variant="outlined">
          <CardContent>
            <Typography className="title" color="textSecondary" gutterBottom>
              <LoadingSkeleton theme="dark" />
            </Typography>

            <div className="cartDetailGrid">
              <div className="thumbnailsection">
                <LoadingSkeleton theme="light" loaderHeight="120px" loaderWidth="100px" />
              </div>
              <div className="productdetailssection">
                <div className="productInfoSection">
                  <div className="editionSection">
                    <p className="productInfoFieldTitle">Edition:</p>
                    <p className="productDetailInnerTitleText">
                      <LoadingSkeleton theme="light" />
                    </p>
                  </div>
                  <div className="publishedSection">
                    <p className="productInfoFieldTitle">Published:</p>
                    <p className="productDetailInnerTitleText">
                      <LoadingSkeleton theme="light" />
                    </p>
                  </div>
                </div>
                <div className="productInfoSection">
                  <div className="languageSection">
                    <p className="productInfoFieldTitle">Language:</p>
                    <p className="productDetailInnerTitleText">
                      <LoadingSkeleton theme="light" />
                    </p>
                  </div>
                  <div className="statusSection">
                    <p className="productInfoFieldTitle">Status:</p>
                    <p className="productDetailInnerTitleText">
                      <LoadingSkeleton theme="light" />
                    </p>
                  </div>
                </div>
                <div className="productInfoSection3">
                  <div className="formatSection">
                    <div className="formatTitleArea">
                      <p className="productInfoFieldTitle">Format:</p>
                      <CustomTooltip
                        fields={{
                          'Tooltip Text': fields.FormatTooltipText,
                        }}
                      />
                    </div>
                    <div>
                      {' '}
                      <LoadingSkeleton theme="light" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="pricedetailssection">
                <p className="priceTitle">Unit Price</p>
                <p className="unitPrice">
                  <LoadingSkeleton theme="light" loaderHeight="30px" />
                </p>
              </div>
            </div>
          </CardContent>
        </StyledCard>
      </Grid>
    );
  }

  function getProductUrl(productDetails: CartProductFieldsProps) {
    let queryString;
    let productType;
    if (productDetails?.productType === ProductType.StatisticsReport) {
      productType = 'statisticsreports';
      queryString = `?reportYear=${productDetails.year}&&term=${productDetails.term}`;
    } else {
      queryString = productDetails.isRecent ? '' : `?edition=${productDetails.edition}`;
      if (productDetails.isDatasheet) {
        productType = 'datasheets';
      } else {
        productType = 'standards';
      }
    }
    return `/${productType}/${productDetails.productId}${queryString}`;
  }

  const fetchData = () => {
    const idpToken = getIdpToken();

    getAPICall(`${process.env.REACT_APP_CART_URL}/cartOrder?lob=${STANDARDS_IDENTIFIER}`, {
      headers: {
        'B2C-Authorization': idpToken,
      },
    })
      .then(function (response: any) {
        if (response && response.data) {
          const items = response.data.items || [];
          setCartItemDetails(items);
          setOrderID(response.data.orderId);

          if (items.length > 0) {
            viewCartEvent({
              currency: 'USD',
              value: items.reduce(
                (total: number, item: CartItemsDetails) =>
                  total + item.priceOption.unitPrice * item.priceOption.quantity,
                0
              ),
              items: items.map((item: CartItemsDetails) => ({
                item_id: `${item.product.productId}`,
                item_name: `${item.product.title}`,
                item_brand: `${item.product.productType}`,
                item_variant: item.priceOption.variantId,
                lob: LOB_DISPLAY_NAMES[STANDARDS_IDENTIFIER],
                price: item.priceOption.unitPrice,
                quantity: item.priceOption.quantity,
                time_in_cart: getTimeInCart(item.dateAdded),
              })),
            });
          }
        }
      })
      .finally(() => {
        setIsLoading(false);
        setIsEmailLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    getShippingCountry();
  }, []);

  function getShippingCountry() {
    let shippingCountry: string = '';
    getAPICall(`${process.env.REACT_APP_CART_URL}/checkout/getshippingcountry`)
      .then(function (response: any) {
        if (response && response.data) {
          shippingCountry = response.data;
        }
      })
      .finally(() => {
        setCountryConfigurationInfo(getCountryConfigurationData(shippingCountry));
      });
  }

  function getCountryConfigurationData(shippingCountry: string) {
    let countryConfigurationData: Array<ProductFormatOptionsFieldProps> = [];

    countryConfigurationData = getCountryConfigFields(shippingCountry, fields.Countries);
    const countryConfigurationFields: IFormatOptionFields | undefined = {
      AllowedFormats: countryConfigurationData[0].fields.AllowedFormats,
    };

    return countryConfigurationFields;
  }

  function getFormatOptions(cartItem: CartItemsDetails) {
    if (cartItem.product?.productType !== ProductType.Standard) {
      const format = cartItem.priceOption?.format;
      return (
        <option key={nextId()} value={format}>
          {format}
        </option>
      );
    }

    let typeSpecificFormats = fields?.TypeSpecificFormats?.find(
      (x) => x.name === cartItem.product?.productType.toLocaleLowerCase()
    );
    return (
      typeSpecificFormats &&
      typeSpecificFormats.fields?.Formats?.map((item: FormatFieldsProps, id: number) => {
        const allowedFormats = countryConfigurationInfo?.AllowedFormats.filter(
          (x: any) => x.name === item.name
        );
        const isDisabled = allowedFormats && allowedFormats.length > 0 ? false : true;

        return (
          <option key={nextId()} value={item.fields.FormatType.value} disabled={isDisabled}>
            {item.fields.FormatType.value}
          </option>
        );
      })
    );
  }

  function getProductDetailsSection(item: CartItemsDetails) {
    let fieldSpecificConfig = getFieldSpecificConfig(item?.product?.productType);
    return (
      <>
        <Grid container className="productdetailssection" spacing={1} key={nextId()}>
          <Grid item xs={6}>
            {fieldSpecificConfig?.HideEdition?.value === false && (
              <div className="editionSection" key={nextId()}>
                <p className="productInfoFieldTitle" key={nextId()}>
                  Edition:
                </p>
                <p className="productDetailInnerTitleText" key={nextId()}>
                  {item.product?.edition}
                </p>
              </div>
            )}
            {fieldSpecificConfig?.HidePeriod?.value === false && (
              <div className="editionSection" key={nextId()}>
                <p className="productInfoFieldTitle" key={nextId()}>
                  Period:
                </p>
                <p className="productDetailInnerTitleText" key={nextId()}>
                  {item.product?.term !== ReportType.Annual ? `${item.product?.term}, ` : ''}
                  {item.product?.year}
                </p>
              </div>
            )}
          </Grid>
          <Grid item xs={6}>
            <div className="publishedSection" key={nextId()}>
              <p className="productInfoFieldTitle" key={nextId()}>
                Published:
              </p>
              <p className="productDetailInnerTitleText" key={nextId()}>
                {item.product?.publishedDate ? item.product.publishedDate.split(' ')[0] : '-'}
              </p>
            </div>
          </Grid>
          <Grid item xs={6}>
            <div className="languageSection" key={nextId()}>
              <p className="productInfoFieldTitle" key={nextId()}>
                Language:
              </p>
              <p className="productDetailInnerTitleText" key={nextId()}>
                {item.priceOption?.language}
              </p>
            </div>
          </Grid>
          <Grid item xs={6}>
            <div className="statusSection key={nextId()}">
              <p className="productInfoFieldTitle" key={nextId()}>
                Status:
              </p>
              <p className="productDetailInnerTitleText" key={nextId()}>
                {item.product?.status}
              </p>
            </div>
          </Grid>
          <Grid item xs={6}>
            <div className="formatSection" key={nextId()}>
              <div className="formatTitleArea" key={nextId()}>
                <p className="productInfoFieldTitle" key={nextId()}>
                  Format:
                </p>
                <CustomTooltip
                  fields={{
                    'Tooltip Text':
                      item.product.productType !== 'StatisticsReport'
                        ? fields.FormatTooltipText
                        : fields.StatisticsRepoFormatTooltipTxt,
                  }}
                />
              </div>
              <div className="formatDropdown" key={nextId()}>
                <StyledFormControl key={nextId()}>
                  <NativeSelect
                    id="demo-customized-select-native"
                    value={item.priceOption.format}
                    onChange={(e) => handleFormatChange(e, item)}
                    input={<BootstrapInput />}
                    key={nextId()}
                  >
                    {getFormatOptions(item)}
                  </NativeSelect>
                </StyledFormControl>
              </div>
            </div>
          </Grid>
          {item.priceOption.format !== 'Print Edition' && (
            <Grid item xs={12}>
              <RecipientEmail
                key={nextId()}
                fields={{
                  RecipientEmailTooltip: fields.RecipientEmailTooltip,
                  EmailPlaceholderText: fields.EmailPlaceholderText,
                  RecipientEmailPlaceholderText: fields.RecipientEmailPlaceholderText,
                  MaxQuantity:
                    fieldSpecificConfig?.EmailRecipientMaxQuantity &&
                    fieldSpecificConfig?.EmailRecipientMaxQuantity.value !== null
                      ? fieldSpecificConfig?.EmailRecipientMaxQuantity.value
                      : item.priceOption.quantity,
                }}
                cartItemFields={{ orderId: orderID, cartItem: item }}
                IsEmailLoading={isEmailLoading}
                emailRemoved={emailRemoved}
              ></RecipientEmail>
            </Grid>
          )}
        </Grid>
        <div className="pricedetailssection" key={nextId()}>
          <p className="priceTitle" key={nextId()}>
            Unit Price
          </p>
          <p className="unitPrice" key={nextId()}>
            {getCurrencyInUSD(item.priceOption?.unitPrice)}
          </p>
          <Quantity
            key={nextId()}
            fields={{ itemMaxQuantity: fieldSpecificConfig?.CartItemMaxQuantity }}
            quantityFields={{
              orderId: orderID,
              cartItem: item,
            }}
            properties={{
              divId: '',
              removeLinkId: '',
            }}
            quantityUpdated={quantityUpdated}
          ></Quantity>
        </div>
      </>
    );
  }

  return (
    <div>
      <RichToastNotification
        key="richToast"
        toastMessages={toastMessages}
        showToast={showToast}
        removeToast={toastRemove}
      ></RichToastNotification>
      <div className="cartDetailsSection">
        {state?.cartCounts?.lobCounts?.ECOMM && state?.cartCounts?.lobCounts?.ECOMM > 0 ? (
          isLoading ? (
            getProductCartSkeleton()
          ) : (
            <Grid container direction="column" justifyContent="space-around" alignItems="stretch">
              {cartItemDetails?.map((item: CartItemsDetails, id: number) => {
                return (
                  <Grid className="productCartGrid" item xs={9} sm={6} md={3} key={nextId()}>
                    {/* className={classes.root} TODO*/}
                    <StyledCard variant="outlined" key={nextId()}>
                      <CardContent key={nextId()}>
                        <Typography
                          className="title"
                          color="textSecondary"
                          gutterBottom
                          key={nextId()}
                        >
                          <div className="header-title-area">
                            <Link
                              to={getProductUrl(item.product)}
                              id={`${item.product.productId}${item.product.edition}`}
                              key={nextId()}
                            >
                              {item.product?.title}
                              {addEllipsis(item.product?.title)}
                            </Link>
                            <Link
                              to="#"
                              onClick={() => handleOpenDialog(item)}
                              className="remove-link"
                            >
                              Remove
                            </Link>
                          </div>
                        </Typography>
                        {/* Confirmation Dialog */}
                        <Dialog open={openDialog} onClose={handleCloseDialog}>
                          <DialogTitle id="alert-dialog-title">
                            {fields.confirmationMessage?.value}
                          </DialogTitle>
                          <DialogActions>
                            <button className="okButton" onClick={handleRemoveItem}>
                              {fields.okButtonText?.value}
                            </button>
                            <button className="cancelButton" onClick={handleCloseDialog} autoFocus>
                              {fields.cancelButtonText?.value}
                            </button>
                          </DialogActions>
                        </Dialog>
                        <div className="cartDetailGrid" key={nextId()}>
                          <div className="thumbnail-main">
                            <div className="thumbnailsection" key={nextId()}>
                              <PreviewStandardImage
                                previewStdFields={{
                                  previewUrl: item.product?.previewUrl,
                                  thumbnailUrl: item.product?.thumbnailUrl,
                                  isDatasheet: item.product?.isDatasheet,
                                }}
                                fields={{
                                  ThumbnailImage: item.product?.isDatasheet
                                    ? fields.DatasheetImage
                                    : fields.ThumbnailImage,
                                }}
                                PreviewBaseUrl={fields?.PreviewBaseUrl}
                                ThumbnailBaseUrl={fields?.ThumbnailBaseUrl}
                              ></PreviewStandardImage>
                            </div>
                            <p className="thumbnailTextArea">
                              {item.product?.isDatasheet
                                ? fields?.DatasheetThumbnailText?.value
                                : fields?.ThumbnailText?.value}
                            </p>
                          </div>
                          {getProductDetailsSection(item)}
                        </div>
                        {getOptInUpgradeRedline(item, state?.loggedIn)}
                      </CardContent>
                    </StyledCard>
                  </Grid>
                );
              })}
            </Grid>
          )
        ) : (
          <div className="empty-cart">
            <RichText field={fields.CartEmptyText} />
          </div>
        )}
      </div>
    </div>
  );
};

export default StandardProductCartDetails;
